import './data-grid-field.js'
import './data-grid-accum-field.js'

import { css, html, LitElement, nothing, PropertyValues } from 'lit'
import { customElement, property, query, state } from 'lit/decorators.js'
import { ifDefined } from 'lit/directives/if-defined.js'
import debounce from 'lodash-es/debounce.js'
import isEmpty from 'lodash-es/isEmpty.js'

import { sleep, parseToNumberOrNull, closestElement } from '@operato/utils'

import { ZERO_CONFIG, ZERO_DATA } from '../configure/zero-config.js'
import { RecordViewHandler } from '../record-view/record-view-handler.js'
import { ColumnConfig, GristConfig, GristData, GristRecord } from '../types.js'
// import { supportsPassive } from '../utils/index.js'
import { dataGridBodyStyle } from './data-grid-body-style.js'
import { DataGridField } from './data-grid-field.js'
import { dataGridBodyClickHandler } from './event-handlers/data-grid-body-click-handler.js'
import { dataGridBodyDblclickHandler } from './event-handlers/data-grid-body-dblclick-handler.js'
import { dataGridBodyFocusChangeHandler } from './event-handlers/data-grid-body-focus-change-handler.js'
import { dataGridBodyContextMenuHandler } from './event-handlers/data-grid-body-contextmenu-handler.js'
import { dataGridBodyKeydownHandler } from './event-handlers/data-grid-body-keydown-handler.js'
import { accumulate } from '../accumulator/accumulator.js'

const THRESHOLD = 300
const DATA_PADDING = 3
const ROW_HEIGHT = 40
const GAP_SIZE = 1

function calcScrollPos(parent: DataGridBody, child: Element) {
  /* getBoundingClientRect는 safari에서 스크롤 상태에서 다른 브라우저와는 다른 값을 리턴함 - 사파리는 약간 이상 작동함. */
  var { top: ct, left: cl, right: cr, bottom: cb } = child.getBoundingClientRect()
  var { top: pt, left: pl, right: pr, bottom: pb } = parent.getBoundingClientRect()
  var { scrollLeft, scrollTop } = parent
  var scrollbarWidth = parent.clientWidth - parent.offsetWidth
  var scrollbarHeight = parent.clientHeight - parent.offsetHeight

  return {
    left: cl < pl ? scrollLeft - (pl - cl) : cr > pr ? scrollLeft - (pr - cr) - scrollbarWidth : undefined,
    top: ct < pt ? scrollTop - (pt - ct) : cb > pb ? scrollTop - (pb - cb) - scrollbarHeight : undefined
  }
}

/* select 타입 붙여넣기: display 또는 value 텍스트와 매칭되는 옵션의 value를 반환 */
function resolveSelectPasteValue(text: string, column: ColumnConfig, record: GristRecord): any {
  const rowOptionField = record[column.record?.rowOptionField || '']
  let { options = [] } = rowOptionField ? rowOptionField : column.record || {}

  /* options가 함수인 경우 호출하여 배열을 가져옴 (arrow function이므로 this는 정의 시점에 바인딩됨) */
  if (typeof options === 'function') {
    try {
      options = (options as Function)()
    } catch (e) {
      return undefined
    }
  }

  if (!Array.isArray(options)) {
    return undefined
  }

  const trimmed = String(text).trim()
  if (!trimmed) return undefined

  for (const opt of options) {
    if (typeof opt === 'string') {
      if (opt.trim() === trimmed) return opt
    } else if (opt && typeof opt === 'object') {
      const display = String((opt as any).display || (opt as any).name || '').trim()
      const val = String((opt as any).value ?? '').trim()
      if (display === trimmed || val === trimmed) return (opt as any).value
    }
  }

  return undefined
}

const ZERO_FOCUS = {
  row: 0,
  column: 0
}

@customElement('ox-grid-body')
export class DataGridBody extends LitElement {
  debounce = debounce((scrollTop: number, clientHeight: number) => {
    const maxVisibleRows = Math.ceil(clientHeight / (ROW_HEIGHT + GAP_SIZE))
    const from = Math.max(0, Math.floor(scrollTop / (ROW_HEIGHT + GAP_SIZE)) - maxVisibleRows * DATA_PADDING)
    const to = Math.min(this.data.records?.length || 0, from + maxVisibleRows * (DATA_PADDING * 2 + 1))

    this.from = from
    this.to = to
  }, THRESHOLD)

  static styles = [
    dataGridBodyStyle,
    css`
      :host {
        font-variation-settings: 'FILL' 1;

        overscroll-behavior: none;
        user-select: none;
      }

      [select-block] {
        position: absolute;
        left: var(--select-box-left);
        top: var(--select-box-top);
        width: var(--select-box-width);
        height: var(--select-box-height);
        border: var(--grid-record-focused-cell-border);
        background-image: var(--focused-background-image);
        pointer-events: none;

        z-index: 5;
      }

      [fixed] {
        position: sticky;
        background-color: var(--grid-record-background-color);
        z-index: 2; /* 고정된 열을 다른 열 위에 표시. */
      }

      :host([raised]) [fixed] {
        /* 고정 컬럼이 살짝 올라와 있는 느낌을 표현 */
        box-shadow: 3px 0 3px rgba(0, 0, 0, 0.1);
      }

      ox-grid-accum-field {
        position: sticky;
        bottom: 0;
        z-index: 1;
      }

      ox-grid-accum-field[fixed] {
        background-color: var(--md-sys-color-primary-container);
      }

      @media (max-width: 768px) {
        /* 모바일에서는 그리드에서 고정된 열 해제 */
        [fixed] {
          position: static;
          z-index: 0;
          box-shadow: none !important;
        }
      }

      @media (pointer: coarse) {
        /* 터치 디바이스에서는 길게 눌러 텍스트 선택 허용 */
        :host {
          user-select: auto;
        }
      }

      /* 한글 IME composition을 위한 숨겨진 input 버퍼 — 기존 에디터 셀 스타일과 일치 */
      #ime-buffer {
        position: absolute;
        opacity: 0;
        z-index: 10;
        background-color: var(--grid-record-editing-background-color, var(--grid-record-background-color, #fff));
        color: var(--md-sys-color-on-background, inherit);
        font: inherit;
        font-size: var(--grid-record-wide-fontsize);
        border: 1px solid transparent;
        border-width: 1px 0;
        border-top: var(--grid-record-editing-border, none);
        border-bottom: var(--grid-record-editing-border, none);
        padding: var(--grid-record-padding);
        box-sizing: border-box;
        outline: none;
        overflow: hidden;
        pointer-events: none;
      }

      #ime-buffer.active {
        opacity: 1;
        pointer-events: auto;
      }
    `
  ]

  @property({ type: Object }) config: GristConfig = ZERO_CONFIG
  @property({ type: Array }) columns: ColumnConfig[] = []
  @property({ type: Object }) data: GristData = ZERO_DATA
  @property({ type: Object }) focused: { row: number; column: number } = ZERO_FOCUS
  @property({ type: Object }) editTarget: { row: number; column: number; valueWith: string | null } | null = null
  @property({ type: Number }) from = -1
  @property({ type: Number }) to = -1
  @property({ type: Array }) fixedLefts: number[] = []

  @state() _selectBlock?: {
    start: DataGridField
    end?: DataGridField
  }

  @query('[select-block]') selectBlock?: HTMLDivElement
  @query('ox-grid-field[focused]') focusedField?: DataGridField

  private _focusedListener?: (e: KeyboardEvent) => void
  private _recordView?: any
  private _recordViewRow?: number
  private _draggable?: boolean
  private _gridElement?: Element | null

  /* IME composition 지원을 위한 숨겨진 input 버퍼 */
  private _imeBuffer?: HTMLInputElement
  _imeComposing: boolean = false
  _imeEditing: boolean = false
  _lastPointerTouch: boolean = false

  resetEdit() {
    this.editTarget = null
  }

  handleOnScroll(e: WheelEvent) {
    const { scrollTop, clientHeight } = e.target as HTMLElement
    this.debounce(scrollTop, clientHeight)
  }

  // issue #13
  // renderOptimisticRow() {
  //   return
  // }

  render() {
    // block이 선택되어 있으면, focused row/column은 표현하지 않는다.
    var { row: focusedRow, column: focusedColumn } = (!this._selectBlock && this.focused) || {}
    var { row: editingRow, column: editingColumn, valueWith = null } = this.editTarget || {}

    var columns = this.columns.filter(column => !column.hidden)
    var data = this.data
    var { records } = data
    var { appendable, classifier, accumulator } = this.config.rows
    const { start, end } = this._selectBlock || {}

    /*
     * 레코드를 추가할 수 있는 경우에는 항상 추가 레코드를 보여준다.
     * 만약, 이전 방식처럼, 커서를 옮겨야만 새로운 레코드가 보이게 하고 싶다면, 조건부를 다음의 코드로 대체한다.
     * -- if (focusedRow == records.length)
     */
    if (appendable) {
      records = [...records, { __dirty__: '+' }]
    }

    if (accumulator) {
      var accumRecord = this.buildAccumulatorRecord()
    }

    return html`
      ${records.map((record, idxRow) => {
        var attrFocusedRow = idxRow === focusedRow
        var attrSelected = record['__selected__']
        var attrOdd = idxRow % 2
        var dirtyFields = record['__dirtyfields__'] || {}
        var { emphasized } = classifier.call(null, record, idxRow) || {}

        return html`
          ${columns.map(
            (column, idxColumn) => html`
              <ox-grid-field
                .data=${data}
                .type=${column.type}
                .rowIndex=${idxRow}
                .columnIndex=${idxColumn}
                .column=${column}
                .record=${record}
                .checked=${record.__selected__ ? 'checked' : record.__check_in_tree__}
                .emphasized=${emphasized}
                ?multiline=${!!column.multiline}
                ?gutter=${column.type == 'gutter'}
                ?odd=${attrOdd}
                ?focused-row=${attrFocusedRow}
                ?selected-row=${attrSelected}
                ?focused=${idxRow === focusedRow && idxColumn === focusedColumn}
                ?editing=${idxRow === editingRow && idxColumn === editingColumn}
                .valueWith=${valueWith}
                .value=${record[column.name]}
                ?dirty=${!!dirtyFields[column.name]}
                fixed=${ifDefined(this.fixedLefts[idxColumn])}
              ></ox-grid-field>
            `
          )}
          <ox-grid-field
            .data=${data}
            .rowIndex=${idxRow}
            .columnIndex=${-1}
            .record=${record}
            .emphasized=${emphasized}
            ?odd=${attrOdd}
            ?focused-row=${attrFocusedRow}
            ?selected-row=${attrSelected}
          ></ox-grid-field>
        `
      })}
      ${accumulator
        ? html`
            ${columns.map(
              (column, idxColumn) => html`
                <ox-grid-accum-field
                  .data=${data}
                  .columnIndex=${idxColumn}
                  .rowIndex=${records.length}
                  .column=${column}
                  .record=${accumRecord!}
                  .value=${accumRecord[column.name]}
                  fixed=${ifDefined(this.fixedLefts[idxColumn])}
                ></ox-grid-accum-field>
              `
            )}
            <ox-grid-accum-field
              .data=${data}
              .columnIndex=${-1}
              .rowIndex=${records.length}
              .record=${accumRecord!}
            ></ox-grid-accum-field>
          `
        : nothing}
      ${start && end && start !== end ? html` <div select-block></div> ` : html``}
      <input id="ime-buffer" type="text" />
      <slot></slot>
    `
  }

  firstUpdated() {
    // TODO issue #13
    // this.addEventListener('scroll', this.handleOnScroll.bind(this))

    /* focus() 를 받을 수 있도록 함. */
    this.setAttribute('tabindex', '-1')

    /* 한글 IME composition을 위한 숨겨진 input 버퍼 설정 */
    const imeBuffer = this.renderRoot.querySelector('#ime-buffer') as HTMLInputElement
    this._imeBuffer = imeBuffer

    imeBuffer.addEventListener('compositionstart', () => {
      this._imeComposing = true
      this._imeEditing = true
      imeBuffer.classList.add('active')
      this._positionIMEBuffer()
    })

    imeBuffer.addEventListener('compositionend', () => {
      this._imeComposing = false
    })

    imeBuffer.addEventListener('input', () => {
      if (!this._imeComposing && !this._imeEditing && imeBuffer.value) {
        /* 영어/숫자: 실제 값이 입력된 경우에만 활성화 */
        this._imeEditing = true
        imeBuffer.classList.add('active')
        this._positionIMEBuffer()
      }
    })

    /*
     * focusout 으로 property를 변경시키는 경우, focusout에 의해 update가 발생하는 경우에는,
     * 그리드 내부의 컴포넌트가 갱신되는 현상을 초래하게 된다.
     * 따라서, focusout 핸들러에서 update를 유발하는 코드는 강력하게 금지시킨다.
     */
    this.addEventListener('focusout', e => {
      /* IME buffer로 포커스가 이동하는 경우 keydown listener 유지 */
      const related = (e as FocusEvent).relatedTarget as Node
      if (related && this.shadowRoot?.contains(related)) {
        return
      }

      /* 그리드 외부로 포커스 이동 시 IME buffer 커밋 */
      if (this._imeEditing) {
        const { row = 0, column = 0 } = this.focused || {}
        this._commitIMEBuffer(row, column)
      }

      if (this._focusedListener) {
        this.removeEventListener('keydown', this._focusedListener)
        delete this._focusedListener
      }
    })

    this.addEventListener('focusin', e => {
      if (!this._focusedListener) {
        this._focusedListener = dataGridBodyKeydownHandler.bind(this)
        this.addEventListener('keydown', this._focusedListener)
      }
    })

    this.addEventListener('set-select-block', async e => {
      e.stopPropagation()

      const { startRow = -1, startColumn = -1, endRow = -1, endColumn = -1 } = ((e as CustomEvent).detail as any) || {}

      const start = this.getFieldByIndex(startRow, startColumn) as DataGridField
      const end = this.getFieldByIndex(endRow, endColumn) as DataGridField

      this.setSelectBlock(start, end)
    })

    this.renderRoot.addEventListener('contextmenu', (event: Event) => {
      const e = event as MouseEvent
      this.setSelectBlock()

      this._draggable = false

      var target = (e.target as Element).closest('ox-grid-field') as DataGridField
      var { rowIndex, columnIndex } = target || {}

      this.dispatchEvent(
        new CustomEvent('focus-change', {
          bubbles: true,
          composed: true,
          detail: {
            row: rowIndex,
            column: columnIndex
          }
        })
      )
    })

    this.renderRoot.addEventListener('pointerdown', (e: Event) => {
      const pe = e as PointerEvent
      const isTouch = pe.pointerType === 'touch'

      /* 터치 이벤트 여부 기록 — 태블릿에서 input focus로 가상 키보드가 뜨는 것을 방지 */
      this._lastPointerTouch = isTouch

      /* 클릭 시 IME buffer가 활성 상태면 먼저 커밋 */
      if (this._imeEditing) {
        const { row = 0, column = 0 } = this.focused || {}
        this._commitIMEBuffer(row, column)
      }

      this.setSelectBlock()

      if (isTouch) {
        /* 터치: click 이벤트에서 처리 (스크롤 시에는 click이 발생하지 않음) */
        return
      }

      if ('buttons' in e && pe.buttons !== 1) {
        return
      }

      e.preventDefault()
      e.stopPropagation()

      this._draggable = true

      var target = (e.target as Element).closest('ox-grid-field') as DataGridField
      var { rowIndex, columnIndex } = target || {}

      this.dispatchEvent(
        new CustomEvent('focus-change', {
          bubbles: true,
          composed: true,
          detail: {
            row: rowIndex,
            column: columnIndex
          }
        })
      )

      if (columnIndex >= 0 && target.editableOnClick && !isNaN(rowIndex) && !isNaN(columnIndex)) {
        this.startEditTarget(rowIndex, columnIndex)
      }
    })

    this.renderRoot.addEventListener('pointermove', (event: Event) => {
      const e = event as PointerEvent
      if (('buttons' in e && e.buttons !== 1) || !this._draggable) {
        return
      }

      e.preventDefault()
      e.stopPropagation()

      const field = e.target as DataGridField
      if (!this._selectBlock) {
        this.setSelectBlock(this.focusedField || field, this.focusedField || field)

        return
      }

      var { start, end } = this._selectBlock || {}

      if (start && end !== field) {
        end = field

        this.setSelectBlock(start, end)
      }
    })

    this.renderRoot.addEventListener('pointerup', (event: Event) => {
      if ((event as PointerEvent).pointerType !== 'touch') {
        event.preventDefault()
        event.stopPropagation()
      }

      this._draggable = false
    })

    this.renderRoot.addEventListener('click', dataGridBodyClickHandler.bind(this))
    this.renderRoot.addEventListener('dblclick', dataGridBodyDblclickHandler.bind(this))
    this.renderRoot.addEventListener('contextmenu', dataGridBodyContextMenuHandler.bind(this))

    this.addEventListener('focus-change', dataGridBodyFocusChangeHandler.bind(this))

    requestAnimationFrame(() => {
      // 1. 공백 찌꺼기가 들어갈 수 있으므로 .trim()으로 깔끔하게 처리
      const primaryColor = getComputedStyle(this).getPropertyValue('--md-sys-color-primary').trim()
      // 2. 가독성을 위해 svg 문자열 조립 분리
      const svgString = `<svg xmlns='http://www.w3.org/2000/svg' width='10' height='10'><rect fill='${primaryColor}' fill-opacity='0.2' x='0' y='0' width='100%' height='100%'/></svg>`

      // 3. Hex 코드의 '#' 기호 및 '< >' 괄호들이 파서를 깨뜨리는 것을 완벽 방지하기 위해 전체 인코딩
      const encodedSvg = encodeURIComponent(svgString)
      this.style.setProperty(
        '--focused-background-image',
        // 4. 비표준인 ;utf8 태그를 떼어내고, 인코딩된 SVG 데이터를 주입
        `url("data:image/svg+xml,${encodedSvg}")`
      )
    })

    this.addEventListener('show-record-view', (e: Event) =>
      this.popupRecordView((e as CustomEvent).detail as { row: number; record: GristRecord })
    )

    this.addEventListener('scroll', function () {
      this.scrollLeft == 0 ? this.removeAttribute('raised') : this.setAttribute('raised', '')
    })
  }

  connectedCallback() {
    super.connectedCallback()
    // 헤더가 ox-grist에 디스패치하는 fetch-params-change 이벤트 수신
    this._gridElement = closestElement('ox-grist', this)
    this._gridElement?.addEventListener('fetch-params-change', this._onFetchParamsChange)
  }

  disconnectedCallback() {
    if (this._gridElement) {
      this._gridElement.removeEventListener('fetch-params-change', this._onFetchParamsChange)
    }
    super.disconnectedCallback()
  }

  // 검색시 스크롤 맨 위로
  _onFetchParamsChange: EventListener = () => {
    this.scrollTop = 0
  }

  getFieldByIndex(rowIndex: number, columnIndex: number, residue: boolean = true) {
    if (rowIndex < 0) {
      return
    }

    var columns = this.columns.filter(column => !column.hidden).length
    if (!residue && columnIndex >= columns) {
      return
    }

    return this.renderRoot.children.item(
      rowIndex * (columns + 1) /* 1 means last dummy column */ + ((columnIndex + columns) % columns)
    ) as DataGridField
  }

  startEditTarget(row: number, column: number, valueWith: string | null = null) {
    var { editable } = this.columns.filter(column => !column.hidden)[column].record
    if (typeof editable === 'function') {
      const curRow = this.data.records[row] || {}
      const curCol = this.columns[column + 1]
      editable = editable.call(this, curRow[curCol.name], curCol, curRow, row, this)
    }

    if (!editable) {
      return
    }

    if (this.editTarget && this.editTarget.row == row && this.editTarget.column == column) {
      return
    }

    /* 에디터 편집 전환 시 IME buffer 정리 */
    this._resetIMEBuffer()

    this.editTarget = {
      row,
      column,
      valueWith
    }
  }

  shouldUpdate(changes: any) {
    if (!changes.has('editTarget')) {
      /*
       * 큰 변화에 대해서는 실제 update가 발생되기 전에 editTarget을 초기화한다.
       */
      this.editTarget = null
    }

    return super.shouldUpdate(changes)
  }

  updated(changes: PropertyValues<this>) {
    if (changes.has('focused')) {
      let element = this.renderRoot?.querySelector('[focused]')
      if (!element) {
        return
      }

      let { top, left } = calcScrollPos(this, element)
      // TODO this.scroll()을 사용하면, 효과가 좋으나 left 계산에 문제가 있는 것 같음.
      // this.scroll({
      //   top,
      //   left,
      //   behavior: 'smooth'
      // })
      if (top !== undefined) {
        this.scrollTop = top
      }
      if (left !== undefined) {
        this.scrollLeft = left
      }

      /* IME buffer 위치 업데이트 */
      this._positionIMEBuffer()
      if (!this.editTarget && this._imeBuffer && !this._lastPointerTouch) {
        this._imeBuffer.focus()
      }
    }

    // 페이징 바뀌면 스크롤 맨 위로
    if (changes.has('data')) {
      const prevData = changes.get('data') as GristData | undefined
      const prevPage = prevData && (prevData as any).page
      const currPage = (this.data as any).page
      if (prevPage !== undefined && prevPage !== currPage) {
        this.scrollTop = 0
      }
    }

    if (this._recordView) {
      this._recordView.record = this.data.records[this._recordViewRow!]
    }
  }

  focus() {
    if (this.editTarget) {
      /* 에디터 편집 모드에서는 에디터가 자체적으로 포커스를 관리하므로 간섭하지 않는다 */
      return
    }

    if (this._imeBuffer && !this._lastPointerTouch) {
      /* 비편집 모드에서는 IME buffer로 포커스 리다이렉트 (터치 시에는 가상 키보드 방지를 위해 제외) */
      this._positionIMEBuffer()
      this._imeBuffer.focus()
    } else {
      super.focus()
    }

    if (this.focused === ZERO_FOCUS) {
      let { records } = this.data
      let row = records.findIndex(record => record['__selected__'])

      this.focused = { row: row == -1 ? 0 : row, column: 0 }
    }
  }

  popupRecordView({ record, row }: { row: number; record: GristRecord }) {
    var titleField = this.config.list.fields[0] || 'name'
    var title = record[titleField]

    /* field가 오브젝트형인 경우에는 렌더러를 타이틀로 사용한다. */
    if (typeof title == 'object') {
      var column = this.config.columns.find(column => column.name == titleField)
      title = column?.record.renderer(title, column, record, row, this /* cautious */)
    }

    this._recordViewRow = row
    this._recordView = RecordViewHandler(
      this.config.columns,
      record,
      row,
      this,
      {
        title
      },
      () => {
        delete this._recordView
        delete this._recordViewRow
      }
    )
  }

  /* 선택된 셀 블록의 값을 HTML(table)과 plain text(TSV) 두 가지 포맷으로 반환 */
  getSelectedBlockValues(): { html: string; text: string } | undefined {
    var { start, end } = this._selectBlock || {}

    if (!(start && end)) {
      start = this.focusedField

      end = start
    }

    if (start && end) {
      const startRowIndex = start.rowIndex < end.rowIndex ? start.rowIndex : end.rowIndex
      const endRowIndex = start.rowIndex < end.rowIndex ? end.rowIndex : start.rowIndex
      const startColumnIndex = start.columnIndex < end.columnIndex ? start.columnIndex : end.columnIndex
      const endColumnIndex = start.columnIndex < end.columnIndex ? end.columnIndex : start.columnIndex

      const columnArray = new Array(endColumnIndex - startColumnIndex + 1).fill(startColumnIndex)
      const columns = this.columns.filter(column => !column.hidden)

      /*
       * 클립보드에 두 가지 포맷(text/html, text/plain)을 동시에 저장한다.
       * 붙여넣기하는 앱이 자신이 지원하는 포맷을 골라 읽는다.
       *   생성되는 HTML:
       *   <td type=object data-value="{&quot;id&quot;:...}">탈수 절임배추</td>
       *                   ^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^  ^^^^^^^^^^^^
       *                   그리드만 읽는 숨겨진 JSON               Excel/메모장이 보는 텍스트
       */
      const rows = new Array(endRowIndex - startRowIndex + 1).fill(startRowIndex).map((start, index) => {
        const rowIndex = start + index
        const record = this.data.records[rowIndex]

        const cells = columnArray.map((start, index) => {
          const columnIndex = start + index
          const column = columns[columnIndex]
          const value = record?.[column.name]
          const type = typeof value

          /* select 타입: value → display 텍스트 미리 계산 (htmlText/plainText 공용) */
          let selectDisplay: any = value
          if (column.type === 'select' && value !== undefined && value !== null) {
            const src = record[column.record?.rowOptionField || ''] ?? column.record
            const options: any[] = Array.isArray(src?.options) ? src.options : []
            const opt = options.find((o: any) => (typeof o === 'string' ? o : String(o?.value ?? '')).trim() === String(value).trim())
            selectDisplay = opt ? (typeof opt === 'string' ? opt : opt.display || opt.name || value) : value
          }

          /* htmlText: object는 JSON 문자열로 보존(data-value) → 그리드 붙여넣기 시 복원됨. select는 display 텍스트 */
          const htmlText = value === undefined || value === null ? '' : type == 'object' ? JSON.stringify(value) : column.type === 'select' ? selectDisplay : value

          /* plainText: ① null/undefined → renderer ② object → name → renderer ③ select → display ④ 기타 → 값 그대로 */
          let plainText: any = ''
          if (value === undefined || value === null) {
            /* 값이 없지만 renderer가 record의 다른 경로로 텍스트를 표시할 수 있음 */
            if (column.record?.renderer) {
              try {
                const r = column.record.renderer(value, column, record, rowIndex, null as any)
                if (typeof r === 'string') plainText = r
              } catch (e) {}
            }
          } else if (type == 'object') {
            /* object: name 필드가 문자열인 경우 우선, 없으면 renderer */
            const rawName = (value as any)?.name
            plainText = typeof rawName === 'string' ? rawName : ''
            if (!plainText && column.record?.renderer) {
              try {
                const r = column.record.renderer(value, column, record, rowIndex, null as any)
                if (typeof r === 'string') plainText = r
              } catch (e) {}
            }
          } else if (column.type === 'select') {
            plainText = selectDisplay
          } else {
            plainText = value
          }

          /* 안전장치: plainText가 object이면 빈 문자열로 대체하여 [object Object] 방지 */
          if (plainText !== null && plainText !== undefined && typeof plainText === 'object') {
            plainText = ''
          }

          return { type, htmlText, plainText }
        })

        return cells
      })

      const escapeHtml = (unsafe: any) => {
        if (unsafe == null) return ''
        return String(unsafe).replace(/[&<>"']/g, function (m) {
          switch (m) {
            case '&':
              return '&amp;'
            case '<':
              return '&lt;'
            case '>':
              return '&gt;'
            case '"':
              return '&quot;'
            case "'":
              return '&#039;'
            default:
              return m
          }
        })
      }

      const html =
        '<table>' +
        rows
          .map(
            cells =>
              '<tr>' +
              cells
                .map(c =>
                  c.type == 'object' && c.htmlText
                    ? `<td type=${c.type} data-value="${escapeHtml(c.htmlText)}">${escapeHtml(c.plainText)}</td>`
                    : `<td type=${c.type}>${escapeHtml(c.htmlText)}</td>`
                )
                .join('') +
              '</tr>'
          )
          .join('') +
        '</table>'

      /* 태그 없는 TSV 포맷 (object는 name만) */
      const text = rows.map(cells => cells.map(c => String(c.plainText)).join('\t')).join('\n')

      return { html, text }
    }
  }

  async copy() {
    const copied = this.getSelectedBlockValues()
    if (!copied) return

    await navigator.clipboard.write([
      new ClipboardItem({
        'text/html': new Blob([copied.html], { type: 'text/html' }),
        'text/plain': new Blob([copied.text], { type: 'text/plain' })
      })
    ])

    const selectBlock = this.selectBlock || this.focusedField
    if (selectBlock) {
      const backgroundColor = selectBlock.style.backgroundColor
      const opacity = selectBlock.style.opacity

      selectBlock.setAttribute('data-tooltip', 'copied to clipboard!')
      const rect = selectBlock.getBoundingClientRect()
      selectBlock.style.setProperty('--tooltip-top', `${rect.top}px`)
      selectBlock.style.setProperty('--tooltip-left', `${rect.left}px`)

      selectBlock.style.backgroundColor = 'red'
      selectBlock.style.opacity = '0.5'
      await sleep(500)
      selectBlock.removeAttribute('data-tooltip')
      selectBlock.style.backgroundColor = backgroundColor
      selectBlock.style.opacity = opacity
    }
  }

  async paste() {
    try {
      const selection = window.getSelection()

      const clipboardItems = await navigator.clipboard.read()
      if (!clipboardItems) {
        return
      }

      var type: string | undefined
      var content: string | undefined

      for (const clipboardItem of clipboardItems) {
        try {
          var blob = await clipboardItem.getType('text/html')
          content = blob && (await blob.text())
          type = 'text/html'
        } catch (e) {
          try {
            blob = await clipboardItem.getType('text/plain')
            content = blob && (await blob.text())
            type = 'text/plain'
          } catch (e) {}
        }

        break
      }

      if (!content) {
        return
      }

      const { row, column } = this.focused
      const { records } = this.data
      const columns = this.columns.filter(column => !column.hidden)

      if (type === 'text/html') {
        const div = document.createElement('div')
        div.innerHTML = content!.trim()
        const table = div.querySelector('table') as HTMLTableElement
        if (!table) {
          return
        }

        if (selection) {
          this.resetEdit()
          selection.removeAllRanges()

          // 포커스가 빠지기 전에 isWorking으로 focusout 이벤트에 대한 값 변경을 막음
          this.focusedField!.isWorking = true
          await this.updateComplete
          this.focusedField!.isWorking = false
        }
        const rows = table.querySelectorAll('tr')

        rows.forEach((record, rowIndex) => {
          if (!(record instanceof HTMLTableRowElement)) {
            return
          }

          var targetRecord = records[row + rowIndex] || { __dirty__: '+' }
          if (row + rowIndex >= records.length) {
            records.push(targetRecord)
          }

          const cells = record.querySelectorAll('td')
          cells.forEach((item, columnIndex) => {
            const targetColumn = columns[column + columnIndex]
            if (!targetColumn || targetColumn.gutterName) return

            /* data-value 속성이 있으면 우선 사용 (그리드에서 복사한 object 데이터 복원용) */
            let value = (item.dataset.value || item.textContent) as any
            const sourceType = item.getAttribute('type') || 'string'
            let type = targetColumn.type || sourceType
            type = type.includes('object') ? 'object' : type // 오브젝트 타입 예외처리
            let { editable } = targetColumn.record
            if (typeof editable === 'function') {
              editable = editable.call(this, value, targetColumn, targetRecord, row, this)
            }

            if (value) {
              switch (type) {
                case 'object':
                  // 오브젝트가 전부 빈값이면 ''로 치환
                  try {
                    value = JSON.parse(value || '{}')
                    value = Object.values(value).every(v => (typeof v === 'string' ? v.trim() === '' : isEmpty(v)))
                      ? null
                      : value
                  } catch (err) {
                    value = null
                  }
                  break
                case 'parameters':
                  try {
                    value = JSON.parse(value || 'null')
                  } catch (err) {
                    value = null
                  }
                  break
                case 'boolean':
                case 'checkbox':
                  value = !!value && !!String(value).match(/true/i)
                  break
                case 'number':
                case 'float':
                case 'integer':
                case 'progress':
                  value = parseToNumberOrNull(value)
                  break
                case 'string':
                  try {
                    const parsed = JSON.parse(value)
                    /* 오브젝트를 문자열 셀에 붙여넣을 때 셀의 표시 텍스트(textContent) 우선 사용 */
                    if (typeof parsed === 'object' && parsed !== null) {
                      value = item.textContent?.trim() || (parsed as any).name || ''
                    } else {
                      value = String(parsed).trim()
                    }
                  } catch (err) {}
                  break
                case 'select':
                  /* select 타입: 붙여넣은 텍스트를 옵션의 display/value와 매칭하여 value로 변환 */
                  value = resolveSelectPasteValue(String(value), targetColumn, targetRecord)
                  break
                default:
                  /* 원본이 string 으로 복사된 값(color-map 등 JSON 문자열 저장 타입)은
                     파싱하지 않고 문자열 그대로 유지 */
                  if (sourceType === 'string' && !item.dataset.value) {
                    value = value?.trim()
                  } else {
                    try {
                      value = JSON.parse(value)
                    } catch (err) {
                      if (type === 'json' || type.includes('object')) value = null
                    }
                  }
              }
            } else {
              value = undefined /* default value 로 설정되도록 하기위해서 undefined로 설정. */
            }

            if (targetColumn && !targetColumn.gutterName && editable) {
              this.dispatchEvent(
                new CustomEvent('field-change', {
                  bubbles: true,
                  composed: true,
                  detail: {
                    before: targetRecord[targetColumn.name],
                    after: value,
                    column: targetColumn,
                    record: targetRecord,
                    row: row + rowIndex
                  }
                })
              )
            }
          })
        })

        return
      } else if (type === 'text/plain') {
        /* TSV 포맷 파싱: 줄바꿈으로 행 분리, 탭으로 열 분리 (엑셀 등 외부 소스 붙여넣기 지원) */
        const tsvRows = content.split('\n')

        tsvRows.forEach((line, rowOffset) => {
          const targetRowIndex = row + rowOffset
          var targetRecord = records[targetRowIndex] || { __dirty__: '+' }
          if (targetRowIndex >= records.length) {
            records.push(targetRecord)
          }

          const tsvCells = line.split('\t')
          tsvCells.forEach((cellValue, colOffset) => {
            const targetColumn = columns[column + colOffset]
            if (!targetColumn || targetColumn.gutterName) return

            let { editable } = targetColumn.record
            if (typeof editable === 'function') {
              editable = editable.call(this, cellValue, targetColumn, targetRecord, targetRowIndex, this)
            }

            if (editable) {
              /* select 타입: 붙여넣은 텍스트를 옵션의 display/value와 매칭하여 value로 변환 */
              let resolvedValue: any = cellValue || undefined
              if (targetColumn.type === 'select' && cellValue) {
                resolvedValue = resolveSelectPasteValue(cellValue, targetColumn, targetRecord)
              }

              this.dispatchEvent(
                new CustomEvent('field-change', {
                  bubbles: true,
                  composed: true,
                  detail: {
                    before: targetRecord[targetColumn.name],
                    after: resolvedValue,
                    column: targetColumn,
                    record: targetRecord,
                    row: targetRowIndex
                  }
                })
              )
            }
          })
        })
      }
    } catch (e) {
      console.log('e : ', e)
    } finally {
      /* paste 완료 후 포커스를 그리드로 복원 */
      this.focus()
    }
  }

  setSelectBlock(start?: DataGridField, end?: DataGridField) {
    if (start?.columnIndex == -1) {
      start = this.getFieldByIndex(start.rowIndex, this.columns.filter(column => !column.hidden).length - 1)
    }

    if (end?.columnIndex == -1) {
      end = this.getFieldByIndex(end.rowIndex, this.columns.filter(column => !column.hidden).length - 1)
    }

    this._selectBlock = start && { start, end }

    if (start && end) {
      window.getSelection()?.removeAllRanges()

      if (start !== end) {
        const left = start.columnIndex < end.columnIndex ? start : end
        const right = left === start ? end : start
        const top = start.rowIndex < end.rowIndex ? start : end
        const bottom = top === start ? end : start

        const { offsetLeft } = left
        const { offsetTop } = top
        const width = right.offsetLeft - offsetLeft + right.offsetWidth
        const height = bottom.offsetTop - offsetTop + bottom.offsetHeight

        this.style.setProperty('--select-box-left', offsetLeft - 1 + 'px')
        this.style.setProperty('--select-box-top', offsetTop - 1 + 'px')
        this.style.setProperty('--select-box-width', width + 'px')
        this.style.setProperty('--select-box-height', height + 'px')
      }

      this.focus()
    }
  }

  buildAccumulatorRecord(): GristRecord {
    var columns = this.columns.filter(column => !column.hidden)

    return columns.reduce((record, column) => {
      if (column.accumulator) {
        record[column.name] = accumulate(this.data, column, column.accumulator)
      }
      return record
    }, {} as GristRecord)
  }

  /** IME buffer를 포커스된 셀 위에 위치시킨다 */
  private _positionIMEBuffer() {
    const focusedEl = this.renderRoot?.querySelector('[focused]') as HTMLElement
    if (focusedEl && this._imeBuffer) {
      this._imeBuffer.style.left = focusedEl.offsetLeft + 'px'
      this._imeBuffer.style.top = focusedEl.offsetTop + 'px'
      this._imeBuffer.style.width = focusedEl.offsetWidth + 'px'
      this._imeBuffer.style.height = focusedEl.offsetHeight + 'px'
    }
  }

  /** IME buffer 값을 field-change 이벤트로 커밋한다 */
  _commitIMEBuffer(row: number, column: number) {
    const text = this._imeBuffer?.value
    this._resetIMEBuffer()

    if (!text) return

    const columns = this.columns.filter(c => !c.hidden)
    const col = columns[column]
    if (!col || col.gutterName) return

    const record = this.data.records[row] || { __dirty__: '+' }

    let { editable } = col.record
    if (typeof editable === 'function') {
      editable = editable.call(this, record[col.name], col, record, row, this)
    }
    if (!editable) return

    /* 타입에 따른 값 변환 */
    let value: any = text
    switch (col.type) {
      case 'number':
      case 'float':
      case 'integer':
      case 'progress':
        value = parseToNumberOrNull(text)
        if (value === null) return
        break
      case 'boolean':
      case 'checkbox':
        return /* boolean은 키보드 텍스트 입력으로 편집 불가 */
    }

    this.dispatchEvent(
      new CustomEvent('field-change', {
        bubbles: true,
        composed: true,
        detail: {
          before: record[col.name],
          after: value,
          column: col,
          record,
          row
        }
      })
    )
  }

  /** IME buffer 취소 (값 버리기) */
  _discardIMEBuffer() {
    this._resetIMEBuffer()
  }

  /** IME buffer 초기화 */
  private _resetIMEBuffer() {
    if (this._imeBuffer) {
      this._imeBuffer.value = ''
      this._imeBuffer.classList.remove('active')
    }
    this._imeEditing = false
    this._imeComposing = false
  }
}
